import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Mail,
  RefreshCw,
  Filter,
  ChevronRight,
  CheckCircle,
  Clock,
  Package,
  Paperclip,
  ExternalLink,
} from 'lucide-react';
import { format } from 'date-fns';
import { clsx } from 'clsx';

const API_BASE = '/api';

// Types
interface EmailSource {
  id: string;
  messageId: string;
  senderEmail: string;
  senderName?: string;
  senderDomain?: string;
  subject?: string;
  bodyPreview?: string;
  receivedAt?: string;
  hasAttachments: boolean;
  attachmentCount: number;
  attachmentNames?: string[];
  orderId?: string;
  processingRunId?: string;
  processed: boolean;
  processedAt?: string;
  createdAt: string;
}

interface EmailStats {
  total: number;
  processed: number;
  unprocessed: number;
  byDomain: Record<string, number>;
  recentCount: number;
}

export default function EmailSourcePage() {
  const [filters, setFilters] = useState({
    senderEmail: '',
    senderDomain: '',
    processed: '',
    page: 1,
    pageSize: 20,
  });

  // Fetch emails
  const { data: emailsData, isLoading, refetch } = useQuery<{
    data: EmailSource[];
    pagination: { totalItems: number; totalPages: number };
  }>({
    queryKey: ['email-sources', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', filters.page.toString());
      params.set('pageSize', filters.pageSize.toString());
      if (filters.senderEmail) params.set('senderEmail', filters.senderEmail);
      if (filters.senderDomain) params.set('senderDomain', filters.senderDomain);
      if (filters.processed) params.set('processed', filters.processed);

      const res = await fetch(`${API_BASE}/emails?${params}`);
      if (!res.ok) throw new Error('Failed to fetch emails');
      return res.json();
    },
    refetchInterval: 10000,
  });

  // Fetch stats
  const { data: statsData } = useQuery<{ data: EmailStats }>({
    queryKey: ['email-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/emails/stats`);
      if (!res.ok) throw new Error('Failed to fetch stats');
      return res.json();
    },
    refetchInterval: 30000,
  });

  const emails = emailsData?.data || [];
  const stats = statsData?.data;
  const pagination = emailsData?.pagination;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Sources</h1>
          <p className="text-gray-500 mt-1">Track incoming order emails and their processing status</p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Emails</p>
                <p className="text-xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Processed</p>
                <p className="text-xl font-bold text-gray-900">{stats.processed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-50 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Unprocessed</p>
                <p className="text-xl font-bold text-gray-900">{stats.unprocessed}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Mail className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Last 24h</p>
                <p className="text-xl font-bold text-gray-900">{stats.recentCount}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Domains */}
      {stats && Object.keys(stats.byDomain).length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Top Sender Domains</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.byDomain).map(([domain, count]) => (
              <button
                key={domain}
                onClick={() => setFilters({ ...filters, senderDomain: domain, page: 1 })}
                className={clsx(
                  'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                  filters.senderDomain === domain
                    ? 'bg-perfectted-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                )}
              >
                {domain} ({count})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>

          <input
            type="text"
            placeholder="Search sender email..."
            value={filters.senderEmail}
            onChange={(e) => setFilters({ ...filters, senderEmail: e.target.value, page: 1 })}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm flex-1 max-w-xs"
          />

          <select
            value={filters.processed}
            onChange={(e) => setFilters({ ...filters, processed: e.target.value, page: 1 })}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="">All Status</option>
            <option value="true">Processed</option>
            <option value="false">Unprocessed</option>
          </select>

          {(filters.senderEmail || filters.senderDomain || filters.processed) && (
            <button
              onClick={() => setFilters({ ...filters, senderEmail: '', senderDomain: '', processed: '', page: 1 })}
              className="text-sm text-perfectted-primary hover:text-perfectted-dark"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Emails List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-perfectted-primary" />
          </div>
        ) : emails.length === 0 ? (
          <div className="text-center py-12">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No emails found</h3>
            <p className="text-gray-500 mt-1">Emails will appear here when received</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sender</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attachments</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Received</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Links</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {emails.map((email) => (
                    <tr key={email.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className={clsx(
                          'flex items-center gap-2 px-2 py-1 rounded-full w-fit',
                          email.processed ? 'bg-green-100' : 'bg-yellow-100'
                        )}>
                          {email.processed ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <Clock className="w-4 h-4 text-yellow-600" />
                          )}
                          <span className={clsx(
                            'text-sm font-medium',
                            email.processed ? 'text-green-600' : 'text-yellow-600'
                          )}>
                            {email.processed ? 'Processed' : 'Pending'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{email.senderName || email.senderEmail}</p>
                          {email.senderName && (
                            <p className="text-xs text-gray-500">{email.senderEmail}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-900 truncate max-w-xs">{email.subject || '(No subject)'}</p>
                      </td>
                      <td className="px-4 py-3">
                        {email.hasAttachments ? (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Paperclip className="w-4 h-4" />
                            <span>{email.attachmentCount}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {email.receivedAt ? format(new Date(email.receivedAt), 'dd MMM HH:mm') : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {email.orderId && (
                            <Link
                              to={`/orders/${email.orderId}`}
                              className="flex items-center gap-1 text-sm text-perfectted-primary hover:text-perfectted-dark"
                            >
                              <Package className="w-4 h-4" />
                              Order
                            </Link>
                          )}
                          {email.processingRunId && (
                            <Link
                              to={`/processing/${email.processingRunId}`}
                              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                            >
                              <ExternalLink className="w-4 h-4" />
                              Run
                            </Link>
                          )}
                          {!email.orderId && !email.processingRunId && (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-200">
              {emails.map((email) => (
                <div key={email.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={clsx(
                          'p-1 rounded-full',
                          email.processed ? 'bg-green-100' : 'bg-yellow-100'
                        )}>
                          {email.processed ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <Clock className="w-4 h-4 text-yellow-600" />
                          )}
                        </div>
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {email.senderName || email.senderEmail}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">{email.subject || '(No subject)'}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span>{email.receivedAt ? format(new Date(email.receivedAt), 'dd MMM HH:mm') : '-'}</span>
                        {email.hasAttachments && (
                          <span className="flex items-center gap-1">
                            <Paperclip className="w-3 h-3" />
                            {email.attachmentCount}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 ml-2">
                      {email.orderId && (
                        <Link
                          to={`/orders/${email.orderId}`}
                          className="flex items-center gap-1 text-xs text-perfectted-primary"
                        >
                          <Package className="w-3 h-3" />
                          Order
                        </Link>
                      )}
                      {email.processingRunId && (
                        <Link
                          to={`/processing/${email.processingRunId}`}
                          className="flex items-center gap-1 text-xs text-blue-600"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Run
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Page {filters.page} of {pagination.totalPages} ({pagination.totalItems} total)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                    disabled={filters.page === 1}
                    className="px-3 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                    disabled={filters.page >= pagination.totalPages}
                    className="px-3 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
